/** API base URL for auth endpoints */
const API_BASE = import.meta.env.VITE_GRAPHQL_URL?.replace("/graphql", "") || "http://localhost:4000";

export interface AuthTokens {
  token: string;
  expiresIn: string;
}

export interface LoginResponse {
  token: string;
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
    tier: string;
    profilePicture?: string;
  };
  expiresIn: string;
  needsPolicyAcceptance: boolean;
}

/**
 * In-memory access token store.
 * Never persisted to localStorage/sessionStorage (XSS protection).
 * Lost on page refresh — recovered via httpOnly refresh cookie.
 */
let accessToken: string | null = null;

export function getAccessToken(): string | null {
  return accessToken;
}

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

/**
 * Parse JWT expiry from the "exp" claim without a library.
 * Returns milliseconds until expiry.
 */
function getTokenExpiryMs(token: string): number {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    const expMs = payload.exp * 1000;
    return expMs - Date.now();
  } catch {
    return 0;
  }
}

/** Timer ID for proactive refresh */
let refreshTimerId: ReturnType<typeof setTimeout> | null = null;

function clearRefreshTimer(): void {
  if (refreshTimerId !== null) {
    clearTimeout(refreshTimerId);
    refreshTimerId = null;
  }
}

/**
 * Schedule proactive token refresh 2 minutes before expiry.
 */
function scheduleProactiveRefresh(token: string, onRefresh: () => Promise<void>): void {
  clearRefreshTimer();
  const msUntilExpiry = getTokenExpiryMs(token);
  const refreshInMs = msUntilExpiry - 2 * 60 * 1000; // 2 min before expiry

  if (refreshInMs <= 0) {
    // Token expires in < 2 min — refresh immediately
    void onRefresh();
    return;
  }

  refreshTimerId = setTimeout(() => {
    void onRefresh();
  }, refreshInMs);
}

/**
 * Verify a magic link token via the web-safe endpoint.
 * Returns access token in body, sets refresh token as httpOnly cookie.
 */
export async function verifyMagicLinkWeb(magicLinkToken: string): Promise<LoginResponse> {
  const res = await fetch(`${API_BASE}/auth/web/verify-magic-link`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include", // send/receive cookies
    body: JSON.stringify({ token: magicLinkToken }),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: "Authentication failed" }));
    throw new Error(body.error || `Authentication failed (${res.status})`);
  }

  return res.json();
}

/**
 * Refresh the access token using the httpOnly refresh cookie.
 * Called on page load (silent refresh) and proactively before expiry.
 */
export async function refreshAccessTokenWeb(): Promise<LoginResponse | null> {
  try {
    const res = await fetch(`${API_BASE}/auth/web/refresh`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      credentials: "include",
    });

    if (!res.ok) {
      return null;
    }

    return res.json();
  } catch {
    return null;
  }
}

/**
 * Logout — clear access token, call backend to revoke + clear cookie.
 */
export async function logoutWeb(): Promise<void> {
  try {
    await fetch(`${API_BASE}/auth/web/logout`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      credentials: "include",
    });
  } catch {
    // Silent fail — logout should always succeed from the user's perspective
  } finally {
    accessToken = null;
    clearRefreshTimer();
  }
}

// ─── OIDC SSO (web platform) ────────────────────────────────────────────────

export type OidcProvider = "google" | "microsoft";

/**
 * Error thrown by `startOidcWeb` carrying the backend's stable error code
 * (e.g. `SSO_PROVIDER_DISABLED`, `OIDC_TEMPORARY_FAILURE`, `INTERNAL_ERROR`) so
 * the UI can map it to a locale-aware message without parsing prose. The code
 * set matches `oidc.error.codes.*`.
 */
export class OidcStartError extends Error {
  readonly code: string;
  constructor(code: string) {
    super(code);
    this.name = "OidcStartError";
    this.code = code;
  }
}

function base64UrlEncode(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/**
 * Generate a PKCE S256 challenge. The backend requires a valid
 * `codeChallenge` (43–128 base64url) on `/auth/oidc/start` even for the web
 * platform — it is the mobile-leg challenge (`codeChallenge_M`) and is never
 * redeemed on the web flow, so the verifier is intentionally discarded here.
 * We still compute a proper S256 challenge from random bytes rather than
 * sending a raw random string, so the value is well-formed and future-proof.
 */
async function generatePkceChallenge(): Promise<string> {
  const verifier = base64UrlEncode(crypto.getRandomValues(new Uint8Array(32)));
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(verifier),
  );
  return base64UrlEncode(new Uint8Array(digest));
}

/**
 * Begin a web SSO flow. POSTs to `/auth/oidc/start` (platform=web), sending
 * `credentials: include` so the backend can set the short-lived
 * `oidc_sso_plat` platform-hint cookie. Returns the IdP authorization URL the
 * caller must full-page navigate to. Throws `OidcStartError` with a stable
 * code on any non-2xx or malformed response.
 *
 * No email is sent to the backend — provider selection is generic to preserve
 * the anti-enumeration contract (we never reveal which IdP a tenant uses).
 */
export async function startOidcWeb(provider: OidcProvider): Promise<string> {
  const codeChallenge = await generatePkceChallenge();

  let res: Response;
  try {
    res = await fetch(`${API_BASE}/auth/oidc/start`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        provider,
        platform: "web",
        codeChallenge,
        codeChallengeMethod: "S256",
      }),
    });
  } catch {
    throw new OidcStartError("OIDC_TEMPORARY_FAILURE");
  }

  if (!res.ok) {
    // Rate limiter (shared /auth bucket) replies 429 with a non-JSON body —
    // surface it as its own code so the UI says "slow down", not "unexpected
    // error". 5xx → temporary failure. Otherwise fall back to the stable code
    // in the JSON body.
    if (res.status === 429) {
      throw new OidcStartError("RATE_LIMITED");
    }
    const body = (await res.json().catch(() => null)) as
      | { error?: string }
      | null;
    if (body?.error) {
      throw new OidcStartError(body.error);
    }
    throw new OidcStartError(
      res.status >= 500 ? "OIDC_TEMPORARY_FAILURE" : "INTERNAL_ERROR",
    );
  }

  const data = (await res.json().catch(() => null)) as
    | { authorizationUrl?: string }
    | null;
  if (!data?.authorizationUrl || typeof data.authorizationUrl !== "string") {
    throw new OidcStartError("INTERNAL_ERROR");
  }
  return data.authorizationUrl;
}

export { scheduleProactiveRefresh, clearRefreshTimer };
